/**
 * PATH: packages/ui/src/hooks/useSidebar.ts
 * FILE_ID: UI-NAV-HOOK-002
 * PURPOSE: Universal sidebar collapse state for every Quotech shell.
 * WHY:
 *   The shared @quotech/ui DashboardLayout (used by master + 12 hub
 *   apps) needs ONE place that owns "is the rail collapsed?". Doing
 *   this in every host invites drift — three weeks ago the email hub
 *   shipped a broken auto-collapse fix because a host-local store
 *   reference outlived the refactor that removed it.
 *
 *   This hook supports two modes:
 *
 *     1. Global (no `hubId` opt) — backward compatible. Reads/writes
 *        `localStorage['sidebar-collapsed']`. Same key as the legacy
 *        callers (src/Pages/Components/Sidebar.jsx, workforce, cfo,
 *        apps/ai/main.tsx). No behaviour change for any caller that
 *        doesn't opt in.
 *
 *     2. Per-hub smart default (`hubId` + `defaultCollapsed`) — the
 *        opt-in path. When a hub passes `defaultCollapsed: true`
 *        AND the user has never manually toggled the rail in this
 *        hub (no "touched" sentinel), `collapsed` starts true. The
 *        FIRST time the user toggles, we stamp the sentinel and
 *        their choice is honoured forever after.
 *
 *        This solves "the email hub's left nav should auto-collapse"
 *        without trapping users who actively prefer the rail open.
 *
 * FLOW (smart-default):
 *   ┌──────────────┐   ┌────────────────────┐   ┌──────────────────┐
 *   │ Mount        │ → │ touched sentinel?  │ → │ collapsed value  │
 *   │ (hubId+def.) │   │   yes → stored     │   │   (rendered)     │
 *   │              │   │   no  → default    │   │                  │
 *   └──────────────┘   └────────────────────┘   └──────────────────┘
 *   ┌────────────┐ ┌──────────────────────┐ ┌──────────────────────┐
 *   │ User toggle│→│ stamp touched + write│→│ honoured next mount  │
 *   └────────────┘ └──────────────────────┘ └──────────────────────┘
 *
 * DEPENDENCIES:
 *   - localStorage. Storage is best-effort — readStorage returns null
 *     when LS throws (Safari private mode, quota), and writes silent-
 *     fail. The fallback in both cases is the in-memory state, which
 *     means the user still gets a working rail even when persistence
 *     is unavailable.
 *
 * RELATED:
 *   - useLayoutState.ts (the only internal consumer)
 *   - apps/email/src/App.tsx (first defaultCollapsed:true caller)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'

const GLOBAL_STORAGE_KEY = 'sidebar-collapsed'

// Durable DB tier (per-user/per-org) on top of the localStorage cache
// below. localStorage gates first paint (instant, offline); the DB makes
// the collapse state follow the operator across devices + survive a
// cache clear. Fail-soft: every server call is best-effort, so when the
// endpoint isn't reachable the rail behaves exactly as the old
// localStorage-only version.
interface SidebarDbState {
  collapsed: boolean
}

function dbScopeKey(hubId: string | undefined): string {
  return hubId ? `shell:sidebar:${hubId}` : 'shell:sidebar'
}

/**
 * Compute the localStorage keys for a hub. Exported so tests can
 * assert against the same shape the hook writes.
 */
export function getSidebarStorageKeys(hubId: string | undefined): {
  collapsed: string
  touched: string | null
} {
  if (!hubId) {
    // Backward-compatible global key — same key the legacy callers
    // (src/Pages/Components/Sidebar.jsx, workforce, cfo, ai/main.tsx)
    // already read/write. No touched sentinel in global mode; we
    // can't know if the writer was the user or another hook.
    return { collapsed: GLOBAL_STORAGE_KEY, touched: null }
  }
  return {
    collapsed: `${GLOBAL_STORAGE_KEY}:${hubId}`,
    touched: `${GLOBAL_STORAGE_KEY}-touched:${hubId}`,
  }
}

function readStorage(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return
    localStorage.setItem(key, value)
  } catch {
    /* noop — quota / private-mode safari */
  }
}

/**
 * Resolve the initial collapsed state. Pure I→O; exported so tests
 * can drive the matrix (hubId × defaultCollapsed × touched × stored)
 * without mounting the hook.
 */
export function resolveInitialCollapsed(opts: {
  hubId?: string
  defaultCollapsed?: boolean
  /** Optional storage reader for tests — defaults to localStorage. */
  read?: (key: string) => string | null
}): boolean {
  const { hubId, defaultCollapsed, read = readStorage } = opts
  const keys = getSidebarStorageKeys(hubId)
  const stored = read(keys.collapsed)
  const touched = keys.touched ? read(keys.touched) : null

  // Per-hub smart-default mode. If the user has NEVER toggled in this
  // hub (touched===null) we honour the host's defaultCollapsed prop.
  // Once they touch, stored wins.
  if (hubId) {
    if (touched === 'true') {
      return stored === 'true'
    }
    if (defaultCollapsed) return true
    return stored === 'true'
  }

  // Global mode — the legacy callers (Sidebar.jsx etc.) just want
  // last-written value. `defaultCollapsed` is intentionally NOT
  // honoured here because we can't distinguish "user hasn't touched"
  // from "another hook wrote false". Hubs that want auto-collapse
  // MUST pass a hubId.
  return stored === 'true'
}

export interface UseSidebarOptions {
  /**
   * Optional namespace for per-hub state. When omitted, the hook
   * falls back to the legacy global key `sidebar-collapsed` and
   * `defaultCollapsed` is ignored (see resolveInitialCollapsed for
   * the rationale).
   */
  hubId?: string
  /**
   * When `hubId` is set, this is the initial collapsed state if the
   * user has never manually toggled the rail in this hub. The user's
   * manual toggle (via the chevron in the sidebar footer) flips a
   * "touched" sentinel that wins forever after.
   *
   * The email hub uses `defaultCollapsed: true` because the inner
   * EmailLayout owns its own rich left rail (Inbox/Starred/Sent/
   * etc.) and double rails crowd the content area.
   */
  defaultCollapsed?: boolean
}

export interface UseSidebarReturn {
  /** Whether the sidebar is collapsed */
  collapsed: boolean
  /** Toggle the collapsed state */
  toggle: () => void
  /** Set the collapsed state directly */
  setCollapsed: (collapsed: boolean) => void
  /** Expand the sidebar */
  expand: () => void
  /** Collapse the sidebar */
  collapse: () => void
}

export function useSidebar(options: UseSidebarOptions = {}): UseSidebarReturn {
  const { hubId, defaultCollapsed } = options

  const keys = useMemo(() => getSidebarStorageKeys(hubId), [hubId])
  const [collapsed, setCollapsedState] = useState<boolean>(() =>
    resolveInitialCollapsed({ hubId, defaultCollapsed }),
  )

  // Set the instant the operator toggles the rail this session. Gates
  // the one-shot DB hydrate so an in-flight GET can't clobber a fresh
  // local choice (which is already being debounce-saved upward).
  const sessionTouchedRef = useRef<boolean>(false)

  // ── Touched sentinel ───────────────────────────────────────
  // We flip this on the FIRST user-driven write (toggle/setCollapsed)
  // so that downstream re-mounts honour the user's choice even when
  // `defaultCollapsed` is true. Persisted only when `hubId` is set;
  // otherwise we're in legacy global mode and the sentinel has no
  // meaning.
  const touchedRef = useRef<boolean>(
    keys.touched ? readStorage(keys.touched) === 'true' : false,
  )

  // ── Re-resolve initial state when hubId changes ────────────
  // Switching `hubId` between mounts (e.g. master shell switches
  // from /jobs to /workforce) should re-evaluate against the new
  // per-hub keys WITHOUT stamping anything. We deliberately don't
  // touch the sentinel here — the previous hub's sentinel stays put.
  useEffect(() => {
    setCollapsedState(resolveInitialCollapsed({ hubId, defaultCollapsed }))
    touchedRef.current = keys.touched
      ? readStorage(keys.touched) === 'true'
      : false
    // We DON'T depend on `defaultCollapsed` because hosts pass a
    // bare boolean literal that can change identity on every render
    // even when the value is the same. The hubId is the actual
    // namespace identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId, keys.collapsed, keys.touched])

  // ── Persist current value ──────────────────────────────────
  useEffect(() => {
    writeStorage(keys.collapsed, String(collapsed))
  }, [collapsed, keys.collapsed])

  // ── One-shot DB hydrate (durable, cross-device) ────────────
  // Re-runs whenever the scope (hubId) changes. localStorage already
  // gated first paint synchronously above; here we reconcile with the
  // server. The server value wins UNLESS the operator already toggled
  // the rail this session (their fresh choice is being debounce-saved
  // upward, so we must not stomp it with the older server snapshot).
  useEffect(() => {
    sessionTouchedRef.current = false
    let cancelled = false
    const scopeKey = dbScopeKey(hubId)
    void fetchServerState<SidebarDbState>(scopeKey, { collapsed: false }).then(
      (server) => {
        if (cancelled || !server || sessionTouchedRef.current) return
        if (typeof server.collapsed === 'boolean') {
          writeStorage(keys.collapsed, String(server.collapsed))
          setCollapsedState(server.collapsed)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [hubId, keys.collapsed])

  // Debounce-save the collapse state to the durable DB tier. Called from
  // the user-driven action handlers (not the render effect) so a
  // server-hydrate-driven state change doesn't echo straight back.
  const persistDb = useCallback(
    (value: boolean) => {
      sessionTouchedRef.current = true
      scheduleServerSave<SidebarDbState>(dbScopeKey(hubId), { collapsed: value })
    },
    [hubId],
  )

  // ── Apply touched sentinel on every USER-DRIVEN change ────
  // Both `toggle` and `setCollapsed` route through here; the
  // `useEffect` above only fires on state change, but the touched
  // stamp is event-driven not state-driven (we want to mark "user
  // chose this value, even if they chose the existing one") so we
  // do it in the actions.
  const stampTouched = useCallback(() => {
    if (!keys.touched) return
    if (touchedRef.current) return
    touchedRef.current = true
    writeStorage(keys.touched, 'true')
  }, [keys.touched])

  const toggle = useCallback(() => {
    stampTouched()
    setCollapsedState((prev) => {
      const next = !prev
      persistDb(next)
      return next
    })
  }, [stampTouched, persistDb])

  const setCollapsed = useCallback(
    (value: boolean) => {
      stampTouched()
      persistDb(value)
      setCollapsedState(value)
    },
    [stampTouched, persistDb],
  )

  const expand = useCallback(() => {
    stampTouched()
    persistDb(false)
    setCollapsedState(false)
  }, [stampTouched, persistDb])

  const collapse = useCallback(() => {
    stampTouched()
    persistDb(true)
    setCollapsedState(true)
  }, [stampTouched, persistDb])

  return {
    collapsed,
    toggle,
    setCollapsed,
    expand,
    collapse,
  }
}

export default useSidebar

